import { ReviewScene, ReviewVisual } from 'modules/match-video/domain/interfaces';
import { INTRO_ZOOM, OUTPUT_HEIGHT, OUTPUT_WIDTH } from './constants';
import { drawFitted } from './draw-fitted';
import { drawPlate, plateFade } from './plates';

function isReviewScene(visual: ReviewVisual): visual is ReviewScene {
  return typeof (visual as Partial<ReviewScene>).draw === 'function';
}

function closePicture(picture: CanvasImageSource) {
  const closable = picture as { close?: () => void };
  if (typeof closable.close === 'function') {
    closable.close();
  }
}

/** A still intro (a template render): fitted with bars, with a slow push-in so it does not read as a frozen frame. */
export function introPictureScene(picture: CanvasImageSource): ReviewScene {
  return {
    draw: (ctx, timeSec, windowSec) => {
      const progress = windowSec > 0 ? Math.min(1, timeSec / windowSec) : 1;
      drawFitted(ctx, picture, OUTPUT_WIDTH, OUTPUT_HEIGHT, { scale: 1 + INTRO_ZOOM * progress });
    },
    dispose: () => closePicture(picture),
  };
}

/** A still plate (a transparent template render): fades and slides in, holds, fades and slides out. */
export function platePictureScene(picture: CanvasImageSource): ReviewScene {
  return {
    draw: (ctx, timeSec, windowSec) => drawPlate(ctx, picture, plateFade(timeSec, windowSec), OUTPUT_WIDTH, OUTPUT_HEIGHT),
    dispose: () => closePicture(picture),
  };
}

/**
 * The scene for what a provider gave: a scene as is, a picture in its still look. `dispose` runs at most once —
 * the assembler calls it after the piece and again on its final clean-up.
 */
export function toReviewScene(visual: ReviewVisual, still: (picture: CanvasImageSource) => ReviewScene): ReviewScene {
  const scene = isReviewScene(visual) ? visual : still(visual);
  let disposed = false;
  return {
    durationSec: scene.durationSec,
    draw: (ctx, timeSec, windowSec) => scene.draw(ctx, timeSec, windowSec),
    dispose: () => {
      if (disposed) return;
      disposed = true;
      scene.dispose();
    },
  };
}
